import React, { useRef, useState } from 'react'
import { Drawer, Box, Text, Button, Group, Stack, Alert, Loader, Progress, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { observer } from 'mobx-react-lite'
import { FiCamera, FiImage, FiVideo, FiArrowLeft, FiArrowRight, FiX } from 'react-icons/fi'
import ShootsEditor from './ShootsEditor'
import ShootsDetailsScreen from './ShootsDetailsScreen'
import { useStore } from '../app/stores/store'
import type { UploadMediaType } from '../app/stores/uploadDrawerStore'

const CameraIcon = FiCamera as React.FC<{ size?: number }>
const ImageIcon = FiImage as React.FC<{ size?: number }>
const VideoIcon = FiVideo as React.FC<{ size?: number }>

const HIDDEN_INPUT: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
  opacity: 0
}

const MAX_VIDEO_BYTES = 100 * 1024 * 1024

function detectMediaType (file: File): UploadMediaType {
  const mime = (file.type || '').toLowerCase()
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('image/')) return 'photo'
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'mp4' || ext === 'webm') return 'video'
  return 'photo'
}

function formatFileSize (bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * ShootsEditor (pinch-to-resize text boxes on the canvas) is on unless explicitly turned off.
 * Set feature flag `shoots-editor-enabled` to false in admin, or `VITE_FEATURE_SHOOTS_EDITOR=false`
 * in env, to post shoots in one step from preview without the editor.
 */
function shootsEditorEnabled (commonFlags: Record<string, boolean>): boolean {
  if (import.meta.env.VITE_FEATURE_SHOOTS_EDITOR === 'true') return true
  if (import.meta.env.VITE_FEATURE_SHOOTS_EDITOR === 'false') return false
  return commonFlags['shoots-editor-enabled'] !== false
}

async function capturePhotoFromCamera (): Promise<File> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' } },
    audio: false
  })
  const video = document.createElement('video')
  video.srcObject = stream
  video.setAttribute('playsinline', 'true')
  video.muted = true
  await video.play()
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
  const w = video.videoWidth || 1280
  const h = video.videoHeight || 720
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    stream.getTracks().forEach((t) => t.stop())
    throw new Error('Canvas is not available.')
  }
  ctx.drawImage(video, 0, 0, w, h)
  stream.getTracks().forEach((t) => t.stop())
  video.srcObject = null

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92)
  )
  if (!blob) throw new Error('Could not capture image.')
  return new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' })
}

const UploadDrawer = observer(function UploadDrawer () {
  const { uploadDrawerStore, commonStore, editorStore } = useStore()
  const desktop = useMediaQuery('(min-width: 769px)')
  const editorOn = shootsEditorEnabled(commonStore.featureFlags)

  const [cameraBusy, setCameraBusy] = useState(false)

  const photoInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const { isOpen, step, selectedFile, previewUrl, mediaType, isUploading, uploadProgress, error } =
    uploadDrawerStore

  const handleClose = () => uploadDrawerStore.close()

  const handleTakePhoto = async () => {
    uploadDrawerStore.clearError()
    setCameraBusy(true)
    try {
      const file = await capturePhotoFromCamera()
      uploadDrawerStore.selectFile(file, 'photo')
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : 'Camera access failed. Try Upload Photo or check browser permissions.'
      uploadDrawerStore.setErrorMessage(msg)
    } finally {
      setCameraBusy(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, forcedType: UploadMediaType) => {
    const file = e.target.files?.[0]
    if (file) {
      const t = forcedType === 'video' ? 'video' : detectMediaType(file)
      if (t === 'video' && file.size > MAX_VIDEO_BYTES) {
        uploadDrawerStore.setErrorMessage('Video too large')
      } else {
        uploadDrawerStore.selectFile(file, t)
      }
    }
    e.target.value = ''
  }

  const drawerTitle = () => {
    if (step === 'options') return 'New shoot'
    if (step === 'preview') return 'Preview'
    if (step === 'details') return 'Details'
    return editorOn ? 'Editor' : 'Editor'
  }

  const renderOptions = () => (
    <Stack spacing="md" pt="xs">
      {error && (
        <Alert color="red" title="Error" withCloseButton onClose={() => uploadDrawerStore.clearError()}>
          {error}
        </Alert>
      )}

      <Button
        variant="filled"
        color="orange"
        size="lg"
        fullWidth
        leftIcon={<CameraIcon size={18} />}
        loading={cameraBusy}
        onClick={() => void handleTakePhoto()}
        sx={{ height: 52, fontWeight: 600 }}
      >
        Take Photo
      </Button>

      <Button
        variant="default"
        size="lg"
        fullWidth
        leftIcon={<ImageIcon size={18} />}
        onClick={() => photoInputRef.current?.click()}
        sx={{ height: 52, fontWeight: 600 }}
      >
        Upload Photo
      </Button>

      <Button
        variant="default"
        size="lg"
        fullWidth
        leftIcon={<VideoIcon size={18} />}
        onClick={() => videoInputRef.current?.click()}
        sx={{ height: 52, fontWeight: 600 }}
      >
        Upload Video
      </Button>

      <Text size="xs" c="dimmed">
        {editorOn
          ? 'Photos and MP4/WebM video (max 100 MB). Preview then edit in the shoots editor.'
          : 'Camera uses your device; uploads accept images and video (max 100 MB for video).'}
      </Text>
    </Stack>
  )

  const renderPreview = () => (
    <Stack spacing="md" pt="xs">
      {error && (
        <Alert color="red" title="Error" withCloseButton onClose={() => uploadDrawerStore.clearError()}>
          {error}
          <Group mt="sm">
            <Button size="xs" variant="light" onClick={() => void uploadDrawerStore.uploadShoot('[]')} disabled={isUploading}>
              Retry upload
            </Button>
          </Group>
        </Alert>
      )}

      <Box
        sx={{
          width: '100%',
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: '#000',
          position: 'relative'
        }}
      >
        {mediaType === 'photo' && previewUrl && (
          <img
            src={previewUrl}
            alt="Preview"
            style={{ width: '100%', maxHeight: 'min(55vh, 420px)', objectFit: 'contain', display: 'block' }}
          />
        )}

        {mediaType === 'video' && previewUrl && (
          <video
            src={previewUrl}
            controls
            playsInline
            preload="metadata"
            style={{ width: '100%', maxHeight: 'min(55vh, 420px)', objectFit: 'contain', display: 'block' }}
          />
        )}
      </Box>

      {selectedFile && (
        <Box
          p="sm"
          sx={{
            borderRadius: 6,
            backgroundColor: 'var(--gs-surface2)',
            border: '1px solid var(--gs-border)'
          }}
        >
          <Text size="sm" fw={600} style={{ wordBreak: 'break-all' }}>
            {selectedFile.name}
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {formatFileSize(selectedFile.size)}
          </Text>
        </Box>
      )}

      {isUploading && (
        <Stack spacing="xs">
          <Group spacing="xs">
            <Loader size="sm" />
            <Text size="sm">Uploading…</Text>
          </Group>
          <Progress value={uploadProgress} size="sm" animated />
        </Stack>
      )}

      <Group position="apart">
        <Button variant="default" leftIcon={<FiArrowLeft size={14} />} onClick={() => uploadDrawerStore.backToOptions()}>
          Back
        </Button>
        {editorOn
          ? (
          <Button variant="filled" color="orange" rightIcon={<FiArrowRight size={14} />} onClick={() => uploadDrawerStore.goToEditor()}>
            Next
          </Button>
            )
          : (
          <Button
            variant="filled"
            color="orange"
            loading={isUploading}
            disabled={isUploading || !selectedFile}
            onClick={() => void uploadDrawerStore.uploadShoot('[]')}
          >
            Post shoot
          </Button>
            )}
      </Group>
    </Stack>
  )

  const renderEditor = () => {
    if (editorOn && selectedFile && previewUrl && (mediaType === 'photo' || mediaType === 'video')) {
      return (
        <Stack spacing="md" pt="xs">
          <ShootsEditor
            photoUrl={previewUrl}
            file={selectedFile}
            mediaType={mediaType}
            editorStore={editorStore}
            onBack={() => uploadDrawerStore.backToPreview()}
            onGoToDetails={() => uploadDrawerStore.goToDetails()}
          />
        </Stack>
      )
    }

    return (
      <Stack spacing="md" pt="xs" align="flex-start">
        <Button variant="subtle" compact leftIcon={<FiArrowLeft size={14} />} onClick={() => uploadDrawerStore.backToPreview()}>
          Back
        </Button>
        <Title order={5}>Editor unavailable</Title>
        <Text size="sm" c="dimmed">
          Turn on <code>VITE_FEATURE_SHOOTS_EDITOR=true</code> or the <code>shoots-editor-enabled</code> feature flag to use the
          shoots editor with photos or video.
        </Text>
        {error && (
          <Alert color="red" withCloseButton onClose={() => uploadDrawerStore.clearError()}>
            {error}
          </Alert>
        )}
      </Stack>
    )
  }

  const renderDetails = () => (
    <Stack spacing="md" pt="xs">
      <ShootsDetailsScreen />
    </Stack>
  )

  return (
    <>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleInputChange(e, 'photo')}
        style={HIDDEN_INPUT}
        aria-hidden
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/webm"
        onChange={(e) => handleInputChange(e, 'video')}
        style={HIDDEN_INPUT}
        aria-hidden
      />

      <Drawer
        opened={isOpen}
        onClose={handleClose}
        position={desktop ? 'right' : 'bottom'}
        size={desktop ? '420px' : '92%'}
        padding="lg"
        withCloseButton={false}
        transitionDuration={250}
        overlayProps={{ opacity: 0.45 }}
        styles={{
          content: {
            height: desktop ? '100vh' : 'auto',
            maxHeight: desktop ? '100vh' : '92vh',
            display: 'flex',
            flexDirection: 'column'
          },
          body: {
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'var(--gs-bg-1)'
          },
          header: {
            backgroundColor: 'var(--gs-bg-1)',
            borderBottom: '1px solid var(--gs-border)',
            marginBottom: 0
          }
        }}
        title={
          <Group position="apart" sx={{ width: '100%' }} noWrap>
            <Button variant="subtle" size="xs" leftIcon={<FiX size={16} />} onClick={handleClose} px={8}>
              Exit
            </Button>
            <Text
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: 0.5,
                flex: 1,
                textAlign: 'center'
              }}
            >
              {drawerTitle()}
            </Text>
            <Box w={72} />
          </Group>
        }
      >
        {step === 'options' && renderOptions()}
        {step === 'preview' && renderPreview()}
        {step === 'editor' && renderEditor()}
        {step === 'details' && renderDetails()}
      </Drawer>
    </>
  )
})

export default UploadDrawer
